export const STORE_COLLECTION_FIELDS = new Set(['promo', 'phone_numbers', 'social_media_urls', 'delivery_fee']);
export const STORE_ITEM_COLLECTION_FIELDS = new Set(['phone_numbers', 'social_media_urls', 'delivery_fee']);

function asRecord(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value;
}

export const CALENDAR_DATE_AUDIT_FIELDS = new Set(['bought_at', 'purchased_at']);

function formatLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Calendar date YYYY-MM-DD in local timezone (no time). */
export function toCalendarDateDisplay(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const d = new Date(trimmed);
    if (!Number.isNaN(d.getTime())) return formatLocalYmd(d);
    return trimmed;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalYmd(value);
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return formatLocalYmd(d);
  return String(value);
}

/** Locale date string (DD/MM/YYYY) without time — for audit UI. */
export function formatCalendarDateDisplay(value) {
  const ymd = toCalendarDateDisplay(value);
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function valuesEqual(a, b, fieldKey) {
  if (fieldKey && CALENDAR_DATE_AUDIT_FIELDS.has(fieldKey)) {
    const da = toCalendarDateDisplay(a);
    const db = toCalendarDateDisplay(b);
    if (da == null && db == null) return true;
    if (da == null || db == null) return false;
    return da === db;
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

export function diffStoreCollectionField(fieldKey, oldValue, newValue) {
  if (!STORE_ITEM_COLLECTION_FIELDS.has(fieldKey)) return [];

  const oldObj = asRecord(oldValue);
  const newObj = asRecord(newValue);
  const changes = [];
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

  for (const itemKey of allKeys) {
    const had = Object.prototype.hasOwnProperty.call(oldObj, itemKey);
    const has = Object.prototype.hasOwnProperty.call(newObj, itemKey);

    if (!had && has) {
      changes.push({ type: 'item_added', itemKey, value: newObj[itemKey] });
    } else if (had && !has) {
      changes.push({ type: 'item_removed', itemKey, value: oldObj[itemKey] });
    } else if (!valuesEqual(oldObj[itemKey], newObj[itemKey])) {
      changes.push({
        type: 'item_changed',
        itemKey,
        from: oldObj[itemKey],
        to: newObj[itemKey],
      });
    }
  }

  return changes;
}

function normalizePhoneReceipt(value) {
  if (typeof value === 'object' && value !== null) {
    return value.include_in_receipt === true || value.include_in_receipt === 'true';
  }
  return value === true || value === 'true';
}

function normalizeSocialEntry(value) {
  if (typeof value === 'object' && value !== null) {
    return {
      url: value.url != null ? String(value.url) : '',
      include_in_receipt: value.include_in_receipt === true || value.include_in_receipt === 'true',
    };
  }
  return { url: value != null ? String(value) : '', include_in_receipt: true };
}

function formatDeliveryFeeItem(itemKey, fee) {
  return { region: itemKey, fee: fee != null ? Number(fee) : null };
}

function formatPhoneItem(itemKey, value) {
  const number = typeof value === 'object' && value !== null && value.number ? value.number : itemKey;
  return { number: String(number), includeInReceipt: normalizePhoneReceipt(value) };
}

function formatSocialItem(itemKey, value) {
  const entry = normalizeSocialEntry(value);
  return { platform: itemKey, url: entry.url, includeInReceipt: entry.include_in_receipt };
}

export function getStoreCollectionItemDisplay(fieldKey, itemChange) {
  if (fieldKey === 'delivery_fee') {
    if (itemChange.type === 'item_changed') {
      return {
        ...formatDeliveryFeeItem(itemChange.itemKey, itemChange.to),
        oldFee: itemChange.from != null ? Number(itemChange.from) : null,
      };
    }
    return formatDeliveryFeeItem(itemChange.itemKey, itemChange.value);
  }

  if (fieldKey === 'phone_numbers') {
    if (itemChange.type === 'item_changed') {
      const next = formatPhoneItem(itemChange.itemKey, itemChange.to);
      const prev = formatPhoneItem(itemChange.itemKey, itemChange.from);
      return { ...next, oldIncludeInReceipt: prev.includeInReceipt };
    }
    return formatPhoneItem(itemChange.itemKey, itemChange.value);
  }

  if (fieldKey === 'social_media_urls') {
    if (itemChange.type === 'item_changed') {
      const next = formatSocialItem(itemChange.itemKey, itemChange.to);
      const prev = formatSocialItem(itemChange.itemKey, itemChange.from);
      return {
        ...next,
        oldUrl: prev.url,
        oldIncludeInReceipt: prev.includeInReceipt,
      };
    }
    return formatSocialItem(itemChange.itemKey, itemChange.value);
  }

  return null;
}

export function isEmptyAuditValue(value) {
  if (value == null) return true;
  if (typeof value === 'object' && !Array.isArray(value)) return Object.keys(value).length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

export function classifyFieldChange(oldValue, newValue, oldObj, newObj, key) {
  if (isEmptyAuditValue(oldValue) && !isEmptyAuditValue(newValue)) return 'added';
  if (!isEmptyAuditValue(oldValue) && isEmptyAuditValue(newValue)) return 'removed';
  if (!oldObj || !(key in oldObj)) return 'added';
  if (!newObj || !(key in newObj)) return 'removed';
  return 'changed';
}

function formatPromoSummary(promo) {
  if (!promo || typeof promo !== 'object') return '';
  const name = String(promo.name || '').trim();
  const pct = promo.percentage != null ? Number(promo.percentage) : null;
  if (name && pct != null && !Number.isNaN(pct)) return `${name} (${pct}%)`;
  if (name) return name;
  return '';
}

function formatPhoneNumbersSummary(phones) {
  if (!phones || typeof phones !== 'object') return '';
  return Object.entries(phones)
    .map(([num, val]) => {
      if (typeof val === 'object' && val !== null) return val.number || num;
      return num;
    })
    .filter(Boolean)
    .join(', ');
}

function formatSocialMediaSummary(links) {
  if (!links || typeof links !== 'object') return '';
  return Object.entries(links)
    .map(([platform, val]) => {
      const url = typeof val === 'object' && val !== null ? val.url : val;
      return url ? `${platform}: ${url}` : platform;
    })
    .filter(Boolean)
    .join(', ');
}

function formatDeliveryFeeSummary(fees) {
  if (!fees || typeof fees !== 'object') return '';
  return Object.entries(fees)
    .map(([region, fee]) => `${region}: ${fee}`)
    .join(', ');
}

export function getStoreFieldSummary(key, value) {
  if (key === 'promo') return formatPromoSummary(value);
  if (key === 'phone_numbers') return formatPhoneNumbersSummary(value);
  if (key === 'social_media_urls') return formatSocialMediaSummary(value);
  if (key === 'delivery_fee') return formatDeliveryFeeSummary(value);
  return '';
}

const STORE_REMOVED_KEYS = {
  promo: 'audit.storeChange.promoRemoved',
  phone_numbers: 'audit.storeChange.phoneNumbersRemoved',
  social_media_urls: 'audit.storeChange.socialMediaRemoved',
  delivery_fee: 'audit.storeChange.deliveryFeeRemoved',
};

const STORE_ADDED_KEYS = {
  promo: 'audit.storeChange.promoAdded',
  phone_numbers: 'audit.storeChange.phoneNumbersAdded',
  social_media_urls: 'audit.storeChange.socialMediaAdded',
  delivery_fee: 'audit.storeChange.deliveryFeeAdded',
};

const STORE_REMOVED_DETAIL_KEYS = {
  promo: 'audit.storeChange.promoRemovedDetail',
  phone_numbers: 'audit.storeChange.phoneNumbersRemovedDetail',
  social_media_urls: 'audit.storeChange.socialMediaRemovedDetail',
  delivery_fee: 'audit.storeChange.deliveryFeeRemovedDetail',
};

const STORE_ADDED_DETAIL_KEYS = {
  promo: 'audit.storeChange.promoAddedDetail',
  phone_numbers: 'audit.storeChange.phoneNumbersAddedDetail',
  social_media_urls: 'audit.storeChange.socialMediaAddedDetail',
  delivery_fee: 'audit.storeChange.deliveryFeeAddedDetail',
};

export function getStoreRemovedMessageKey(key, oldValue) {
  if (!STORE_COLLECTION_FIELDS.has(key)) return null;
  const summary = getStoreFieldSummary(key, oldValue);
  if (summary) return STORE_REMOVED_DETAIL_KEYS[key] || null;
  return STORE_REMOVED_KEYS[key] || null;
}

export function getStoreCollectionItemChanges(change) {
  if (!change || !STORE_ITEM_COLLECTION_FIELDS.has(change.key)) return [];

  const { key, type, oldValue, newValue } = change;
  if (type === 'changed') return diffStoreCollectionField(key, oldValue, newValue);
  if (type === 'added') return diffStoreCollectionField(key, {}, newValue || {});
  if (type === 'removed') return diffStoreCollectionField(key, oldValue || {}, {});
  return [];
}

function formatWebsiteAuditValue(value) {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return {
      url: value.url != null ? String(value.url) : '',
      include_in_receipt: value.include_in_receipt || 'none',
    };
  }
  return { url: String(value), include_in_receipt: 'none' };
}

export function formatStoreAuditScalarValue(fieldKey, value, { t }) {
  if (value == null) return '—';

  if (fieldKey === 'promo') {
    const summary = formatPromoSummary(value);
    return summary || '—';
  }

  if (fieldKey === 'website') {
    const website = formatWebsiteAuditValue(value);
    if (!website.url) return '—';
    const receiptMode = website.include_in_receipt;
    const modeLabel = receiptMode === 'link'
      ? t('settings.showAsLink')
      : receiptMode === 'qr'
        ? t('settings.showAsQr')
        : t('settings.dontInclude');
    return `${website.url} (${modeLabel})`;
  }

  if (fieldKey === 'logo_path') {
    const path = String(value).trim();
    if (!path) return '—';
    const filename = path.split('/').pop();
    return filename || t('audit.storeAudit.logoUpdated');
  }

  if (fieldKey === 'tax') {
    const num = Number(value);
    return Number.isFinite(num) ? `${num.toFixed(2)}%` : '—';
  }

  if (fieldKey === 'include_delivery_fee' || fieldKey === 'requires_ref_num' || fieldKey === 'discount_requires_approval') {
    return value === true || value === 'true' ? t('common.yes') : t('common.no');
  }

  if (typeof value === 'boolean') {
    return value ? t('common.yes') : t('common.no');
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toLocaleString() : '—';
  }

  if (typeof value === 'string') {
    return value.trim() || '—';
  }

  return String(value);
}

export function isStoreCollectionField(key) {
  return STORE_ITEM_COLLECTION_FIELDS.has(key) || key === 'promo';
}

/** Normalize category/product color hex for display (#RGB → #RRGGBB). */
export function normalizeHexColor(value) {
  if (value == null || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return null;
}

export function getStoreAddedMessageKey(key, newValue) {
  if (!STORE_COLLECTION_FIELDS.has(key)) return null;
  const summary = getStoreFieldSummary(key, newValue);
  if (summary) return STORE_ADDED_DETAIL_KEYS[key] || null;
  return STORE_ADDED_KEYS[key] || null;
}
